/* eslint-disable no-underscore-dangle */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	InputAdornment,
	InputBase,
	List,
	ListItem,
	ListItemText,
	Paper,
	Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/styles';
import { getSelectedProject, getAuth } from 'src/selectors';
import {
	fetchCustomersData,
	fetchLotsPaginateData,
	fetchReminderPaginateData,
	fetchSalePaginateData,
	fetchStagesPaginateData,
	fetchPackagesPaginateData
} from 'src/api';
import { getPrimaryPhone, priceFormatter } from 'src/utils';

const SEARCH_SECTIONS = [
	{ id: 'lots', name: 'Lot' },
	{ id: 'sales', name: 'Sale' },
	{ id: 'contacts', name: 'Contact' },
	{ id: 'stages', name: 'Stage' },
	{ id: 'packages', name: 'Package' },
	{ id: 'reminders', name: 'Reminders' }
];

const PAGE_SIZE = 10;
const PANEL_WIDTH = 640;

const emptySectionState = () => ({
	items: [],
	page: 0,
	hasMore: false,
	total: 0,
	expanded: true,
	loadingMore: false
});

const emptyResults = () =>
	SEARCH_SECTIONS.reduce((acc, section) => {
		acc[section.id] = emptySectionState();
		return acc;
	}, {});

const useStyles = makeStyles((theme) => ({
	root: {
		padding: 0,
		width: '300px',
		marginRight: theme.spacing(1.5),
		position: 'relative'
	},
	inputRoot: {
		width: '100%',
		height: 36,
		padding: '0 12px',
		borderRadius: 18,
		backgroundColor: 'rgba(255,255,255,0.08)',
		border: '1px solid rgba(255,255,255,0.18)',
		color: 'rgba(255,255,255,0.92)',
		transition: 'background-color 0.15s ease, border-color 0.15s ease',
		'&:hover': {
			backgroundColor: 'rgba(255,255,255,0.12)',
			borderColor: 'rgba(255,255,255,0.28)'
		},
		'&.Mui-focused': {
			backgroundColor: 'rgba(255,255,255,0.12)',
			borderColor: 'rgba(255,255,255,0.45)'
		}
	},
	inputField: {
		padding: '0 4px',
		fontSize: 13,
		color: 'rgba(255,255,255,0.95)',
		'&::placeholder': {
			opacity: 1,
			color: 'rgba(255,255,255,0.45)'
		}
	},
	searchIcon: {
		fontSize: 18,
		color: 'rgba(255,255,255,0.55)',
		marginRight: 2
	},
	spinner: {
		color: 'rgba(255,255,255,0.7)'
	},
	panel: {
		position: 'fixed',
		width: PANEL_WIDTH,
		maxWidth: 'min(640px, calc(100vw - 16px))',
		maxHeight: '70vh',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		zIndex: theme.zIndex.modal + 10
	},
	panelHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: theme.spacing(1, 1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
		flexShrink: 0,
		background: '#fff'
	},
	panelTitle: {
		fontWeight: 600,
		fontSize: '0.95rem'
	},
	content: {
		overflowY: 'auto',
		padding: theme.spacing(1, 1.5, 1.5),
		background: '#fff'
	},
	section: {
		marginBottom: theme.spacing(1)
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		padding: theme.spacing(0.5, 0),
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		textAlign: 'left',
		'&:hover': {
			opacity: 0.85
		}
	},
	sectionHeaderLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	sectionTitle: {
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		color: theme.palette.text.secondary,
		fontSize: '0.7rem'
	},
	count: {
		fontSize: '0.7rem',
		color: theme.palette.text.secondary
	},
	chevron: {
		fontSize: 18,
		color: theme.palette.text.secondary
	},
	listItem: {
		borderRadius: 4,
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	showMore: {
		marginTop: theme.spacing(0.5),
		textTransform: 'none',
		fontSize: '0.8rem'
	},
	sectionEmpty: {
		padding: theme.spacing(1, 0.5),
		fontSize: '0.8rem',
		color: theme.palette.text.secondary
	},
	hint: {
		padding: theme.spacing(2, 1),
		textAlign: 'center',
		color: theme.palette.text.secondary,
		fontSize: '0.875rem'
	},
	loadingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1, 0),
		color: theme.palette.text.secondary
	}
}));

function getContacts(sale) {
	return (sale.purchaserContact || [])
		.map((el) => (sale.companyPurchaser ? el.companyName : el.fullName))
		.join(', ');
}

function getOptionLabel(sectionId, option) {
	switch (sectionId) {
		case 'contacts':
			return `${option?.fullName || ''} (${option?.email || ''}) ${
				getPrimaryPhone(option?.phones) || ''
			}`.trim();
		case 'lots':
			return `#${option?.humanId || ''} (${option?.stage?.number || ''}) ${priceFormatter(
				option?.price
			)} ${option?.sqm != null ? `${option.sqm}m2` : ''}`.trim();
		case 'stages':
			return `${option?.number || ''} ${option?.name ? `(${option.name})` : ''}`.trim();
		case 'sales':
			return `#${option?.lotNumber?.humanId || ''} ${getContacts(option)}`.trim();
		case 'packages':
			return `#${option?.lot?.humanId?.toString() || ''} ${option?.houseType?.name || ''} (${
				option?.houseType?.builder?.name || ''
			})`.trim();
		case 'reminders':
			return `${option?.note || ''} (${option?.type || ''})`.trim();
		default:
			return '';
	}
}

function getPath(sectionId, item) {
	switch (sectionId) {
		case 'contacts':
			return `/admin/contacts/${item.id}`;
		case 'lots':
			return `/admin/lots/${item.id}`;
		case 'stages':
			return `/admin/stages/${item.id}`;
		case 'sales':
			return `/admin/sales-details/${item.id}`;
		case 'packages':
			return `/admin/packages/${item.id}`;
		case 'reminders':
			return `/admin/reminders/${item.id}`;
		default:
			return null;
	}
}

function parseSectionResponse(sectionId, res) {
	const data = res?.data || {};
	const items = data.items || [];
	const pagination = data.pagination || {};
	const total =
		pagination.itemsTotalCount ??
		data.itemsTotalCount ??
		items.length;
	const page = Number(pagination.page) || 1;
	const totalPages =
		Number(pagination.totalPages) ||
		Math.max(1, Math.ceil(total / PAGE_SIZE));
	const hasMore =
		pagination.hasNextPage != null
			? Boolean(pagination.hasNextPage)
			: page < totalPages;

	return { items, page, hasMore, total };
}

function SearchBar() {
	const classes = useStyles();
	const dispatch = useDispatch();
	const selectedProject = useSelector(getSelectedProject);
	const userId = useSelector(getAuth);

	const rootRef = useRef(null);
	const inputRef = useRef(null);
	const requestIdRef = useRef(0);
	const runSearchRef = useRef(async () => {});

	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState(emptyResults);
	const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

	const updatePanelPos = () => {
		if (!rootRef.current) return;
		const rect = rootRef.current.getBoundingClientRect();
		const width = Math.min(PANEL_WIDTH, window.innerWidth - 16);
		let left = rect.right - width;
		if (left < 8) left = 8;
		setPanelPos({ top: rect.bottom + 6, left });
	};

	useLayoutEffect(() => {
		if (!open) return undefined;
		updatePanelPos();
		window.addEventListener('resize', updatePanelPos);
		window.addEventListener('scroll', updatePanelPos, true);
		return () => {
			window.removeEventListener('resize', updatePanelPos);
			window.removeEventListener('scroll', updatePanelPos, true);
		};
	}, [open]);

	useEffect(() => {
		setResults(emptyResults());
	}, [selectedProject]);

	const fetchSectionPage = async (sectionId, searchString, page) => {
		const filters = { search: searchString };
		switch (sectionId) {
			case 'lots':
				// Include Settled lots in global search (list page defaults to excluding them)
				return dispatch(
					fetchLotsPaginateData(selectedProject, page, PAGE_SIZE, {
						...filters,
						settled: true
					})
				);
			case 'sales':
				return dispatch(fetchSalePaginateData(selectedProject, page, PAGE_SIZE, filters));
			case 'contacts':
				return dispatch(fetchCustomersData(selectedProject, page, PAGE_SIZE, filters));
			case 'stages':
				return dispatch(fetchStagesPaginateData(selectedProject, page, PAGE_SIZE, filters));
			case 'packages':
				return dispatch(
					fetchPackagesPaginateData(selectedProject, page, PAGE_SIZE, filters)
				);
			case 'reminders':
				return dispatch(
					fetchReminderPaginateData(selectedProject, page, PAGE_SIZE, filters)
				);
			default:
				return null;
		}
	};

	runSearchRef.current = async (searchString) => {
		const trimmed = (searchString || '').trim();
		if (!selectedProject || !trimmed) {
			setResults(emptyResults());
			setLoading(false);
			return;
		}

		const requestId = ++requestIdRef.current;
		setLoading(true);

		const settled = await Promise.all(
			SEARCH_SECTIONS.map(async (section) => {
				try {
					const res = await fetchSectionPage(section.id, trimmed, 1);
					return { id: section.id, ...parseSectionResponse(section.id, res) };
				} catch (error) {
					return {
						id: section.id,
						items: [],
						page: 1,
						hasMore: false,
						total: 0
					};
				}
			})
		);

		if (requestId !== requestIdRef.current) {
			return;
		}

		const next = emptyResults();
		settled.forEach((sectionResult) => {
			next[sectionResult.id] = {
				items: sectionResult.items,
				page: sectionResult.page,
				hasMore: sectionResult.hasMore,
				total: sectionResult.total,
				expanded: sectionResult.items.length > 0,
				loadingMore: false
			};
		});
		setResults(next);
		setLoading(false);
	};

	const debouncedSearch = useRef(
		_.debounce((value) => {
			runSearchRef.current(value);
		}, 700)
	).current;

	useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		if (!query?.trim()) {
			setResults(emptyResults());
			setLoading(false);
			debouncedSearch.cancel();
			return undefined;
		}
		debouncedSearch(query);
		return undefined;
	}, [open, query, debouncedSearch, selectedProject, userId]);

	const handleClose = () => {
		setOpen(false);
	};

	const handleChange = (event) => {
		setQuery(event.target.value);
		setOpen(true);
	};

	const handleSelect = (sectionId, item) => {
		const path = getPath(sectionId, item);
		if (path) {
			window.open(path, '_blank', 'noopener,noreferrer');
		}
		inputRef.current?.focus();
	};

	const toggleExpanded = (sectionId) => {
		setResults((prev) => ({
			...prev,
			[sectionId]: {
				...prev[sectionId],
				expanded: !prev[sectionId].expanded
			}
		}));
	};

	const handleShowMore = async (sectionId) => {
		const trimmed = query.trim();
		const section = results[sectionId];
		if (!trimmed || !section?.hasMore || section.loadingMore) return;

		const nextPage = (section.page || 1) + 1;
		const requestId = requestIdRef.current;

		setResults((prev) => ({
			...prev,
			[sectionId]: { ...prev[sectionId], loadingMore: true }
		}));

		try {
			const res = await fetchSectionPage(sectionId, trimmed, nextPage);
			if (requestId !== requestIdRef.current) return;

			const parsed = parseSectionResponse(sectionId, res);
			setResults((prev) => {
				const current = prev[sectionId] || emptySectionState();
				const existingIds = new Set(current.items.map((item) => item.id));
				const appended = parsed.items.filter((item) => !existingIds.has(item.id));
				return {
					...prev,
					[sectionId]: {
						...current,
						items: [...current.items, ...appended],
						page: parsed.page,
						hasMore: parsed.hasMore,
						total: parsed.total || current.total,
						loadingMore: false
					}
				};
			});
		} catch (error) {
			if (requestId === requestIdRef.current) {
				setResults((prev) => ({
					...prev,
					[sectionId]: { ...prev[sectionId], loadingMore: false }
				}));
			}
		}
	};

	const totalCount = SEARCH_SECTIONS.reduce(
		(sum, section) => sum + (results[section.id]?.items?.length || 0),
		0
	);
	const hasQuery = Boolean(query?.trim());

	const panel = open
		? ReactDOM.createPortal(
				<Paper
					className={classes.panel}
					elevation={8}
					style={{ top: panelPos.top, left: panelPos.left }}
				>
					<Box className={classes.panelHeader}>
						<Typography className={classes.panelTitle}>Search All</Typography>
						<IconButton
							aria-label="Close search"
							size="small"
							onClick={handleClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
					<div className={classes.content}>
						{!hasQuery && (
							<Typography className={classes.hint}>
								Type to search Lot, Sale, Contact, Stage, Package and Reminders.
							</Typography>
						)}
						{hasQuery && loading && totalCount === 0 && (
							<div className={classes.loadingRow}>
								<CircularProgress size={18} />
								Searching…
							</div>
						)}
						{hasQuery &&
							!loading &&
							SEARCH_SECTIONS.map((section) => {
								const sectionState = results[section.id] || emptySectionState();
								const items = sectionState.items || [];
								const countLabel =
									sectionState.total != null && sectionState.total !== ''
										? `${items.length}${
												sectionState.hasMore ||
												items.length < sectionState.total
													? ` / ${sectionState.total}`
													: ''
										  }`
										: `${items.length}${sectionState.hasMore ? '+' : ''}`;

								return (
									<div
										key={section.id}
										className={classes.section}
									>
										<button
											type="button"
											className={classes.sectionHeader}
											onClick={() => toggleExpanded(section.id)}
										>
											<span className={classes.sectionHeaderLeft}>
												{sectionState.expanded ? (
													<ExpandLessIcon className={classes.chevron} />
												) : (
													<ExpandMoreIcon className={classes.chevron} />
												)}
												<Typography className={classes.sectionTitle}>
													{section.name}
												</Typography>
											</span>
											<Typography className={classes.count}>
												{countLabel}
											</Typography>
										</button>
										<Divider />
										<Collapse in={sectionState.expanded}>
											{items.length === 0 ? (
												<Typography className={classes.sectionEmpty}>
													No results
												</Typography>
											) : (
												<>
													<List
														disablePadding
														dense
													>
														{items.map((item) => (
															<ListItem
																key={`${section.id}-${item.id}`}
																button
																className={classes.listItem}
																onMouseDown={(event) =>
																	event.preventDefault()
																}
																onClick={() =>
																	handleSelect(section.id, item)
																}
															>
																<ListItemText
																	primary={getOptionLabel(
																		section.id,
																		item
																	)}
																/>
															</ListItem>
														))}
													</List>
													{sectionState.hasMore && (
														<Button
															color="primary"
															size="small"
															className={classes.showMore}
															disabled={sectionState.loadingMore}
															onClick={() =>
																handleShowMore(section.id)
															}
														>
															{sectionState.loadingMore ? (
																<>
																	<CircularProgress
																		size={14}
																		style={{ marginRight: 8 }}
																	/>
																	Loading…
																</>
															) : (
																'Show more'
															)}
														</Button>
													)}
												</>
											)}
										</Collapse>
									</div>
								);
							})}
					</div>
				</Paper>,
				document.body
		  )
		: null;

	return (
		<div
			className={clsx(classes.root)}
			ref={rootRef}
		>
			<InputBase
				className={classes.inputRoot}
				classes={{ input: classes.inputField }}
				fullWidth
				placeholder="Search..."
				value={query}
				inputRef={inputRef}
				onFocus={() => setOpen(true)}
				onChange={handleChange}
				startAdornment={
					<InputAdornment position="start">
						<SearchIcon className={classes.searchIcon} />
					</InputAdornment>
				}
				endAdornment={
					loading ? (
						<InputAdornment position="end">
							<CircularProgress
								className={classes.spinner}
								size={16}
							/>
						</InputAdornment>
					) : null
				}
			/>
			{panel}
		</div>
	);
}

export default SearchBar;
